import { FC } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { useTranslation } from 'react-i18next';
import { routes, SupportedLanguage } from '../../config/routes';
import { serviceSchema, breadcrumbSchema } from '../../utils/schemas';

const ISO22000: FC = () => {
  const { t, i18n } = useTranslation();
  const currentLang = (i18n.language?.substring(0, 2) || 'en') as SupportedLanguage;

  return (
    <div className="iso-page">
      <Helmet>
        <title>{t('iso22000.meta.title')}</title>
        <meta name="description" content={t('iso22000.meta.description')} />
        <meta name="keywords" content={t('iso22000.meta.keywords')} />
        <script type="application/ld+json">
          {JSON.stringify(serviceSchema('ISO 22000 Certification - Food Safety', 'ISO 22000 Food Safety Management System certification.', '/services/iso/iso-22000'))}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(breadcrumbSchema([
            { name: 'Home', path: '/' },
            { name: 'Services', path: '/services' },
            { name: 'ISO Certifications', path: '/services/iso' },
            { name: 'ISO 22000', path: '/services/iso/iso-22000' }
          ]))}
        </script>
      </Helmet>

      {/* Hero Section */}
      <section className="iso-hero">
        <div className="container">
          <h1>{t('iso22000.hero.title')}</h1>
          <p className="iso-subtitle">{t('iso22000.hero.subtitle')}</p>
          <Link
            to={routes.applyOnline[currentLang]}
            className="btn btn-primary btn-quote"
          >
            {t('common.applyOnline')}
          </Link>
        </div>
      </section>

      {/* What is ISO 22000 */}
      <section className="section section-white">
        <div className="container">
          <h2 className="section-title">{t('iso22000.whatIs.title')}</h2>
          <p className="iso-text">{t('iso22000.whatIs.description')}</p>
          <div className="iso-role-box">
            <h3>{t('iso22000.whatIs.roleBox.title')}</h3>
            <p>{t('iso22000.whatIs.roleBox.description')}</p>
          </div>
        </div>
      </section>

      {/* Key Benefits */}
      <section className="section section-gray">
        <div className="container">
          <h2 className="section-title">{t('iso22000.benefits.title')}</h2>
          <div className="benefits-grid">
            <div className="benefit-card">
              <div className="benefit-number">1</div>
              <h3>{t('iso22000.benefits.benefit1.title')}</h3>
              <p>{t('iso22000.benefits.benefit1.description')}</p>
            </div>
            <div className="benefit-card">
              <div className="benefit-number">2</div>
              <h3>{t('iso22000.benefits.benefit2.title')}</h3>
              <p>{t('iso22000.benefits.benefit2.description')}</p>
            </div>
            <div className="benefit-card">
              <div className="benefit-number">3</div>
              <h3>{t('iso22000.benefits.benefit3.title')}</h3>
              <p>{t('iso22000.benefits.benefit3.description')}</p>
            </div>
          </div>
        </div>
      </section>

      {/* Industry Focus */}
      <section className="section section-white">
        <div className="container">
          <h2 className="section-title">{t('iso22000.industryFocus.title')}</h2>
          <div className="industry-focus-list">
            <div className="industry-focus-item">
              <h3>{t('iso22000.industryFocus.foodProduction.title')}</h3>
              <p>{t('iso22000.industryFocus.foodProduction.description')}</p>
            </div>
            <div className="industry-focus-item">
              <h3>{t('iso22000.industryFocus.beverages.title')}</h3>
              <p>{t('iso22000.industryFocus.beverages.description')}</p>
            </div>
            <div className="industry-focus-item">
              <h3>{t('iso22000.industryFocus.packaging.title')}</h3>
              <p>{t('iso22000.industryFocus.packaging.description')}</p>
            </div>
            <div className="industry-focus-item">
              <h3>{t('iso22000.industryFocus.distribution.title')}</h3>
              <p>{t('iso22000.industryFocus.distribution.description')}</p>
            </div>
            <div className="industry-focus-item">
              <h3>{t('iso22000.industryFocus.horeca.title')}</h3>
              <p>{t('iso22000.industryFocus.horeca.description')}</p>
            </div>
            <div className="industry-focus-item">
              <h3>{t('iso22000.industryFocus.agriculture.title')}</h3>
              <p>{t('iso22000.industryFocus.agriculture.description')}</p>
            </div>
          </div>
        </div>
      </section>

      {/* MSC Advantage */}
      <section className="section section-gray">
        <div className="container">
          <h2 className="section-title">{t('iso22000.mscAdvantage.title')}</h2>
          <div className="advantage-cards">
            <div className="advantage-card">
              <h3>{t('iso22000.mscAdvantage.specializedAuditors.title')}</h3>
              <p>{t('iso22000.mscAdvantage.specializedAuditors.description')}</p>
            </div>
            <div className="advantage-card">
              <h3>{t('iso22000.mscAdvantage.practicalAudit.title')}</h3>
              <p>{t('iso22000.mscAdvantage.practicalAudit.description')}</p>
            </div>
            <div className="advantage-card">
              <h3>{t('iso22000.mscAdvantage.objectiveCertification.title')}</h3>
              <p>{t('iso22000.mscAdvantage.objectiveCertification.description')}</p>
            </div>
          </div>
        </div>
      </section>

      {/* Certification Process */}
      <section className="section section-white">
        <div className="container">
          <h2 className="section-title">{t('iso22000.process.title')}</h2>
          <div className="process-timeline steps-6">
            <div className="process-step">
              <div className="process-step-number">1</div>
              <h3>{t('iso22000.process.step1')}</h3>
            </div>
            <div className="process-step">
              <div className="process-step-number">2</div>
              <h3>{t('iso22000.process.step2')}</h3>
            </div>
            <div className="process-step">
              <div className="process-step-number">3</div>
              <h3>{t('iso22000.process.step3')}</h3>
            </div>
            <div className="process-step">
              <div className="process-step-number">4</div>
              <h3>{t('iso22000.process.step4')}</h3>
            </div>
            <div className="process-step">
              <div className="process-step-number">5</div>
              <h3>{t('iso22000.process.step5')}</h3>
            </div>
            <div className="process-step">
              <div className="process-step-number">6</div>
              <h3>{t('iso22000.process.step6')}</h3>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="section-cta-final">
        <div className="container">
          <h2>{t('iso22000.cta.title')}</h2>
          <p>{t('iso22000.cta.description')}</p>
          <div className="cta-buttons">
            <Link to={routes.quoteForm[currentLang].replace(':isoCode', 'iso-22000')} className="btn btn-primary">{t('common.getIsoQuote', { isoCode: 'ISO 22000' })}</Link>
          </div>
          <p className="cta-footer">{t('iso22000.cta.footer')}</p>
        </div>
      </section>
    </div>
  );
};

export default ISO22000;
